// Verbatim content — "Rethinking Content and Commerce: The composable case for separation"
// Source PDF text preserved; whitespace normalised for readability on the web.

export default {
  lede:
    "A revolution is happening in the field of digital commerce, in how we structure our e-commerce platforms, and it has the potential to transform how businesses operate online. Yes, we are talking about the separation of content and commerce data. A shift is emerging today, where content and commerce data are separated into distinct systems. This 'composable approach' argues that decoupling these layers facilitates agility, scalability, and efficiency.",
  chapters: [
    {
      title: 'Content v/s Commerce: Understanding the Difference',
      sections: [
        {
          heading: 'Content Data',
          body:
            '• Product descriptions and specifications\n• Category structures and relationships\n• Customer reviews and user-generated content\n• Images, videos, and other media\n• Marketing copy and educational materials\n• Blogs and email newsletter content'
        },
        {
          heading: 'Commerce Data',
          body:
            '• Inventory levels\n• Order processing\n• Customer accounts\n• Pricing information\n• Payment transactions\n• Shipping details'
        },
        {
          heading: 'Why the tangle hurts',
          body:
            'Content data includes everything that tells the story of your products, whereas commerce data focuses on the business side. When these two very different types of data are tangled together in one system, neither gets the attention it deserves. Your marketing team struggles with clunky content tools built into your commerce platform, while your developers have to navigate complex commerce logic just to update a product description. There are too many inevitable dependencies within your product teams. So when one department lags, the others lag too.'
        }
      ]
    },
    {
      title: 'The Benefits of Breaking Up',
      sections: [
        {
          heading: 'Significant Cost Reduction',
          body:
            'Use the right tools for the right jobs — no wasted features, better scalability, and more efficient teams.'
        },
        {
          heading: 'Cleaner Code and Better Performance',
          body:
            'Faster content delivery, secure commerce, easier testing, and systems optimised for their own strengths.'
        },
        {
          heading: 'Development at the Speed of Business',
          body:
            'Faster content updates, quicker rollouts, easier experimentation, and agility to meet market demands.'
        },
        {
          heading: 'Happier, More Productive Teams',
          body:
            'Each team works in purpose-built tools, avoids bottlenecks, and grows deeper expertise without overlap.'
        }
      ]
    },
    {
      title: 'How Real Brands Are Making the Switch',
      sections: [
        {
          heading: 'The New Balance Success Story',
          body:
            "With operations in 120+ countries, the athletic footwear and apparel giant New Balance's monolithic platform was slowing them down. Marketing teams couldn't keep up with regional needs, content was scattered, and brand consistency suffered. Even small updates required developer support, costing time and momentum.\n\nThe solution? They separated content from commerce by adopting a headless architecture. The result? More speed. More flexibility. A unified brand across markets.\n\nThey made the leap. Have you?"
        }
      ]
    },
    {
      title: 'Making the Switch: What You Need to Know',
      sections: [
        {
          heading: 'Building the Right Tech Stack — Content Management',
          body:
            '• Headless CMS platforms like Contentful, Sanity, or Prismic\n• Traditional systems with API capabilities like WordPress or Drupal\n• Specialised PIM systems for product information like Akeneo or Salsify'
        },
        {
          heading: 'Building the Right Tech Stack — Commerce Operations',
          body:
            '• API-first commerce platforms like Commerce Layer, commercetools, or Elastic Path\n• Headless options from established platforms like Shopify Plus or BigCommerce\n• Open-source solutions like Medusa or Fabric'
        },
        {
          heading: 'Take a Phased Implementation Approach',
          body:
            "You don't have to rip everything out at once. A phased approach can be implemented to make the whole process smoother. This also ensures that there are no bottlenecks later.\n\n• Start by identifying where the pain is — slow content updates, poor flexibility, etc.\n• Pick one product category or feature to pilot the new setup.\n• Gradually expand to more products and features as you go with the process.\n• Keep refining the process based on what you learn from previous steps."
        },
        {
          heading: 'Prepare Your Teams for Change',
          body:
            "Tech is only half the story. The real game-changers are the people who make or break the transformation.\n\n• Clearly redefine roles between content, design and tech teams.\n• Train teams on new tools and workflows so they're set up for success.\n• Celebrate early wins to keep the momentum going.\n• Most importantly, keep teams talking — especially if they were siloed before."
        }
      ]
    },
    {
      title: 'The Future Is Composable',
      sections: [
        {
          heading: 'Gartner’s outlook',
          body:
            'By 2026, Gartner predicts that the speed of digital innovation will improve by 60%, relative to 2022, for businesses that have established mechanisms to reuse composable digital commerce modules. The companies that embrace this approach now will gain a significant competitive advantage.'
        },
        {
          heading: 'Is Your Brand Ready?',
          body:
            "Take a moment to consider your current setup:\n\n• Is your marketing team frustrated with your commerce platform's content tools?\n• Do simple content updates require developer involvement?\n• Are you paying for features in your platform that you barely use?\n• Does your site load slowly because it's weighed down by unnecessary commerce logic?\n\nIf you answered yes to any of these questions, it might be time to consider separating your content and commerce data. Let's connect and work it out."
        }
      ]
    }
  ],
  pullQuote:
    'Every hour marketing spends fighting the commerce platform is an hour they are not spending fighting for the customer.',
  nwLink:
    'Getting a composable estate to move at its potential pace is as much about people and process as it is about architecture. NeuralWorks is one of the tools we lean on, alongside the practitioners who have run these migrations before.'
};
